import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Stack,
  Typography,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import EmailIcon from "@mui/icons-material/Email";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { toast } from "react-toastify";

import AppEmailModal from "../common/AppEmailModal";
import AppButton from "../buttons/AppButton";
import AppCircularProgress from "../common/AppCircularProgress";

import {
  getInvoicePdf,
  emailInvoice,
} from "../../redux/actions/invoiceActions";
import { clearPdfUrl } from "../../redux/slices/invoice";

const InvoicePdfTab = ({ invoice }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { pdfUrl, loadingPdf, errorLoadingPdf, loadingEmail } = useSelector(
    (state) => state.invoice,
  );

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  useEffect(() => {
    if (invoice?.id && !pdfUrl && !loadingPdf) {
      dispatch(getInvoicePdf(invoice.id));
    }
  }, [dispatch, invoice?.id, pdfUrl, loadingPdf]);

  useEffect(() => {
    return () => {
      dispatch(clearPdfUrl());
    };
  }, [dispatch]);

  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `Invoice_${invoice.invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleMenuClose();
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 2000);
      }, 100);
    };
    handleMenuClose();
  };

  const handleShare = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => toast.success("Link copied to clipboard!"));
    handleMenuClose();
  };

  const handleEmailClick = () => {
    setEmailModalOpen(true);
    handleMenuClose();
  };

  const handleEmailSubmit = async (email) => {
    const success = await dispatch(emailInvoice(invoice.id, { email: email }));
    if (success) {
      setEmailModalOpen(false);
    }
  };

  // --- RENDER FUNCTIONS FOR ACTIONS ---
  const renderDesktopActions = () => (
    <Stack direction="row" spacing={1.5} mb={3} justifyContent="flex-end">
      <AppButton
        variant="outlined"
        color="primary"
        startIcon={<ShareIcon />}
        onClick={handleShare}
      >
        Share Link
      </AppButton>
      <AppButton
        variant="outlined"
        color="secondary"
        startIcon={<EmailIcon />}
        onClick={handleEmailClick}
      >
        Email
      </AppButton>
      <AppButton
        variant="contained"
        color="warning"
        startIcon={<FileDownloadIcon />}
        onClick={handleDownload}
        disabled={!pdfUrl || loadingPdf}
      >
        Download
      </AppButton>
      <AppButton
        variant="contained"
        color="primary"
        startIcon={<PrintIcon />}
        onClick={handlePrint}
        disabled={!pdfUrl || loadingPdf}
      >
        Print Invoice
      </AppButton>
    </Stack>
  );

  const renderMobileActions = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
        px: 1,
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
        Document Actions
      </Typography>
      <Stack direction="row" spacing={0.5}>
        <IconButton
          color="primary"
          onClick={handlePrint}
          disabled={!pdfUrl || loadingPdf}
        >
          <PrintIcon />
        </IconButton>
        <IconButton color="primary" onClick={handleShare}>
          <ShareIcon />
        </IconButton>
        <IconButton
          color="primary"
          onClick={handleDownload}
          disabled={!pdfUrl || loadingPdf}
        >
          <FileDownloadIcon />
        </IconButton>
        <IconButton color="primary" onClick={handleEmailClick}>
          <EmailIcon />
        </IconButton>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Conditionally render actions based on screen size */}
      {isMobile ? renderMobileActions() : renderDesktopActions()}

      {/* PDF Viewer */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: "650px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#1b1b1b" : "#e0e0e0",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {loadingPdf ? (
          <AppCircularProgress />
        ) : errorLoadingPdf ? (
          <>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography color="error">{errorLoadingPdf}</Typography>
            <AppButton
              variant="outlined"
              color="error"
              sx={{ mt: 2 }}
              onClick={() => dispatch(getInvoicePdf(invoice.id))}
            >
              Retry
            </AppButton>
          </>
        ) : pdfUrl ? (
          <iframe
            src={`${pdfUrl}#toolbar=0&view=FitH`}
            title={`Invoice PDF ${invoice?.invoiceNumber}`}
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        ) : null}
      </Paper>

      {/* Email Modal */}
      <AppEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={handleEmailSubmit}
        loading={loadingEmail}
        title="Email Document"
        description={`Send a copy of document #${invoice.invoiceNumber} directly to an email.`}
      />
    </Box>
  );
};

export default InvoicePdfTab;
